import { css, type SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { LAYOUT2 } from '@/Client/Styles/LAYOUT2';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import {
  FOCUSED_MODULE_BOX_STYLE,
  FOCUSED_MODULE_EMPTY_STYLE,
  FOCUSED_MODULE_HEADER_STYLE,
  FOCUSED_MODULE_TITLE_STYLE,
} from '../../FocusedScopeEditor/focusedModuleStyles';

/**
 * The prototype's `.ta-*` table as a real `<table>`: a labelled header strip, one inset rule per
 * row, and a timeline rail through the mark column. The table sits inset by `FRAME_INSET`; the
 * header strip and section bands reach back out to the card's edge so they still read as running
 * edge to edge.
 */
export type ActivityLogTableStyles = {
  root: SerializedStyles;
  header: SerializedStyles;
  title: SerializedStyles;
  count: SerializedStyles;
  empty: SerializedStyles;
  frame: SerializedStyles;
  table: SerializedStyles;
  visuallyHidden: SerializedStyles;
  columnHeading: SerializedStyles;
  firstColumnHeading: SerializedStyles;
  lastColumnHeading: SerializedStyles;
  yearBand: SerializedStyles;
  sectionHeaderCell: SerializedStyles;
  firstSectionHeaderCell: SerializedStyles;
  lastSectionHeaderCell: SerializedStyles;
  sectionTitle: SerializedStyles;
  expandableFirstCell: SerializedStyles;
  expandableRow: SerializedStyles;
  expandCaretButton: SerializedStyles;
  sectionTitleRow: SerializedStyles;
  sectionMark: SerializedStyles;
  cell: SerializedStyles;
  lastRowCell: SerializedStyles;
  lastColumnCell: SerializedStyles;
  whenColumn: SerializedStyles;
  whenCell: SerializedStyles;
  day: SerializedStyles;
  month: SerializedStyles;
  ordinal: SerializedStyles;
  time: SerializedStyles;
  markColumn: SerializedStyles;
  markCell: SerializedStyles;
  firstMarkCell: SerializedStyles;
  lastMarkCell: SerializedStyles;
  mark: SerializedStyles;
  whoColumn: SerializedStyles;
  whoCell: SerializedStyles;
  name: SerializedStyles;
  alertName: SerializedStyles;
  detail: SerializedStyles;
  artifactColumn: SerializedStyles;
  artifactCell: SerializedStyles;
};

const FRAME_INSET = '16px';
const COLUMN_GAP = '14px';
/** The mark column's box, in px — shared so a view's marker (e.g. the task status badge) can size itself to it. */
export const ACTIVITY_LOG_TABLE_MARK_SIZE = 18;
const MARK_SIZE = `${ACTIVITY_LOG_TABLE_MARK_SIZE}px`;

const name = css`
  label: ACTIVITY_LOG_TABLE_NAME;
  font-size: ${pxToRem(15)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.3;
  color: ${COLORS.ink};
`;

const muted = css`
  font-size: ${pxToRem(14)};
  line-height: 1.35;
  color: ${COLORS.textTertiary};
`;

export const DEFAULT_ACTIVITY_LOG_TABLE_STYLES: ActivityLogTableStyles = {
  root: css`
    ${FOCUSED_MODULE_BOX_STYLE};
    label: ACTIVITY_LOG_TABLE;
    padding: 0;
    gap: 0;
    font-family: ${FONT.body};
  `,
  header: css`
    ${FOCUSED_MODULE_HEADER_STYLE};
    label: ACTIVITY_LOG_TABLE_HEADER;
    padding: 14px ${FRAME_INSET} 12px;
  `,
  title: css`
    ${FOCUSED_MODULE_TITLE_STYLE};
    label: ACTIVITY_LOG_TABLE_TITLE;
  `,
  count: css`
    label: ACTIVITY_LOG_TABLE_COUNT;
    margin-left: auto;
    font-size: ${pxToRem(13)};
    color: ${COLORS.textTertiary};
  `,
  empty: css`
    ${FOCUSED_MODULE_EMPTY_STYLE};
    label: ACTIVITY_LOG_TABLE_EMPTY;
    padding: 0 ${FRAME_INSET} ${FRAME_INSET};
  `,
  frame: css`
    label: ACTIVITY_LOG_TABLE_FRAME;
    padding: 0 ${FRAME_INSET};
  `,
  table: css`
    label: ACTIVITY_LOG_TABLE_TABLE;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  `,
  visuallyHidden: css`
    label: ACTIVITY_LOG_TABLE_VISUALLY_HIDDEN;
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  `,
  columnHeading: css`
    label: ACTIVITY_LOG_TABLE_COLUMN_HEADING;
    padding: 9px ${COLUMN_GAP} 9px 0;
    background: ${COLORS.stroke};
    text-align: left;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(9)};
    font-weight: ${FONT_WEIGHT.bold};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${COLORS.textSecondary};
  `,
  firstColumnHeading: css`
    label: ACTIVITY_LOG_TABLE_FIRST_COLUMN_HEADING;
    box-shadow: -${FRAME_INSET} 0 0 0 ${COLORS.stroke};
  `,
  lastColumnHeading: css`
    label: ACTIVITY_LOG_TABLE_LAST_COLUMN_HEADING;
    padding-right: 0;
    box-shadow: ${FRAME_INSET} 0 0 0 ${COLORS.stroke};
  `,
  yearBand: css`
    label: ACTIVITY_LOG_TABLE_YEAR_BAND;
    padding: 16px 0 10px;
    text-align: left;
    font-size: ${pxToRem(13)};
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.ink};
    border-bottom: 1px solid ${COLORS.ink};
  `,
  // half an entry row's height, banded and ruled above and below in the entries' separator color
  sectionHeaderCell: css`
    label: ACTIVITY_LOG_TABLE_SECTION_HEADER_CELL;
    padding-top: ${LAYOUT2.spacingHalf}px;
    padding-bottom: ${LAYOUT2.spacingHalf}px;
    background: ${COLORS.sectionBand};
    border-top: 1px solid ${COLORS.stroke};
    border-bottom: 1px solid ${COLORS.stroke};
  `,
  // the band and its rules carried out across the frame inset to the card's edge: a separator-colored
  // copy of the cell, under a band-colored one a pixel shorter at top and bottom, leaving the rules
  firstSectionHeaderCell: css`
    label: ACTIVITY_LOG_TABLE_FIRST_SECTION_HEADER_CELL;
    box-shadow: calc(-${FRAME_INSET} - 1px) 0 0 -1px ${COLORS.sectionBand}, -${FRAME_INSET} 0 0 0 ${COLORS.stroke};
  `,
  lastSectionHeaderCell: css`
    label: ACTIVITY_LOG_TABLE_LAST_SECTION_HEADER_CELL;
    box-shadow: calc(${FRAME_INSET} + 1px) 0 0 -1px ${COLORS.sectionBand}, ${FRAME_INSET} 0 0 0 ${COLORS.stroke};
  `,
  // the entries' mark box without its white ground: there's no rail to mask on the band
  sectionMark: css`
    label: ACTIVITY_LOG_TABLE_SECTION_MARK;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${MARK_SIZE};
  `,
  // a collapsed group's caret sits at the first cell's left edge, clear of its right-aligned date
  expandableFirstCell: css`
    label: ACTIVITY_LOG_TABLE_EXPANDABLE_FIRST_CELL;
    position: relative;
  `,
  expandableRow: css`
    label: ACTIVITY_LOG_TABLE_EXPANDABLE_ROW;
    cursor: pointer;
  `,
  // the Kai Navigator's group caret button
  expandCaretButton: css`
    label: ACTIVITY_LOG_TABLE_EXPAND_CARET_BUTTON;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 3px;
    background: transparent;
    color: ${COLORS.textTertiary};
    cursor: pointer;
    transition: background 120ms ease, color 120ms ease;

    &:hover {
      color: ${COLORS.ink};
      background: ${withOpacity(COLORS.ink, 0.06)};
    }
  `,
  // the title at the left, its aside (a total, say) pushed to the right
  sectionTitleRow: css`
    label: ACTIVITY_LOG_TABLE_SECTION_TITLE_ROW;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${LAYOUT2.spacing1}px;
  `,
  // a size above the entries' names
  sectionTitle: css`
    label: ACTIVITY_LOG_TABLE_SECTION_TITLE;
    font-size: ${pxToRem(16)};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.3;
    color: ${COLORS.ink};
  `,
  cell: css`
    label: ACTIVITY_LOG_TABLE_CELL;
    padding: 15px ${COLUMN_GAP} 15px 0;
    vertical-align: middle;
    border-bottom: 1px solid ${COLORS.stroke};
  `,
  lastRowCell: css`
    label: ACTIVITY_LOG_TABLE_LAST_ROW_CELL;
    border-bottom: 0;
  `,
  lastColumnCell: css`
    label: ACTIVITY_LOG_TABLE_LAST_COLUMN_CELL;
    padding-right: 0;
  `,
  whenColumn: css`
    label: ACTIVITY_LOG_TABLE_WHEN_COLUMN;
    width: calc(86px + ${COLUMN_GAP});
  `,
  whenCell: css`
    label: ACTIVITY_LOG_TABLE_WHEN_CELL;
    text-align: right;
    line-height: 1.25;
  `,
  day: css`
    label: ACTIVITY_LOG_TABLE_DAY;
    display: block;
    font-size: ${pxToRem(15)};
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.textSecondary};
  `,
  month: css`
    label: ACTIVITY_LOG_TABLE_MONTH;
    font-size: ${pxToRem(11)};
    letter-spacing: 0.04em;
    color: ${COLORS.textTertiary};
    margin-right: 3px;
  `,
  ordinal: css`
    label: ACTIVITY_LOG_TABLE_ORDINAL;
    font-size: ${pxToRem(10)};
  `,
  time: css`
    label: ACTIVITY_LOG_TABLE_TIME;
    display: block;
    margin-top: 1px;
    font-size: ${pxToRem(13)};
    color: ${COLORS.textTertiary};
  `,
  markColumn: css`
    label: ACTIVITY_LOG_TABLE_MARK_COLUMN;
    width: calc(${MARK_SIZE} + ${COLUMN_GAP});
  `,
  markCell: css`
    label: ACTIVITY_LOG_TABLE_MARK_CELL;
    position: relative;
    vertical-align: middle;

    &::before {
      content: '';
      position: absolute;
      top: 9px;
      bottom: 9px;
      left: calc(${MARK_SIZE} / 2 - 0.5px);
      width: 1px;
      background: ${COLORS.stroke};
    }
  `,
  firstMarkCell: css`
    label: ACTIVITY_LOG_TABLE_FIRST_MARK_CELL;

    &::before {
      top: 50%;
    }
  `,
  lastMarkCell: css`
    label: ACTIVITY_LOG_TABLE_LAST_MARK_CELL;

    &::before {
      bottom: 50%;
    }
  `,
  mark: css`
    label: ACTIVITY_LOG_TABLE_MARK;
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${MARK_SIZE};
    min-height: ${MARK_SIZE};
    background: ${COLORS.white};
    box-shadow: 0 0 0 5px ${COLORS.white};
  `,
  whoColumn: css`
    label: ACTIVITY_LOG_TABLE_WHO_COLUMN;
    width: calc(92px + ${COLUMN_GAP});
  `,
  whoCell: css`
    label: ACTIVITY_LOG_TABLE_WHO_CELL;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${COLORS.textTertiary};
  `,
  name,
  alertName: css`
    ${name};
    label: ACTIVITY_LOG_TABLE_ALERT_NAME;
    color: ${COLORS.error};
  `,
  detail: css`
    ${muted};
    label: ACTIVITY_LOG_TABLE_DETAIL;
    margin-top: 4px;
  `,
  artifactColumn: css`
    label: ACTIVITY_LOG_TABLE_ARTIFACT_COLUMN;
    width: 150px;
  `,
  artifactCell: css`
    ${muted};
    label: ACTIVITY_LOG_TABLE_ARTIFACT_CELL;
  `,
};
